import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React from 'react'
import { useDownloadItemActions, useProtectedFile } from '../hooks'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { ProtectedFileModel } from '../models'
import { AuthFetchError } from '@e3dc-react/shell/Types'
import { FetchErrorCodes } from '@e3dc-react/shell/Libs'
import { Typography } from '@e3dc-react/shell/Elements/Typography'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { PROTECTED_FILE_QUERY_PARAM } from 'src/Libs/navigation/consts'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { Box, Stack } from '@mui/material'
import { J2Documentcopy } from '@e3dc-react/icons'
import DownloadItemBreadcrumbs from './search-file/DownloadItemBreadcrumbs'

export interface ProtectedFileDialogProps {
  protectedFileName: string
  deleteFileNameToken: () => void
  onClose?: () => void
}

interface DialogContentProps {
  fileName: string
  fileData?: ProtectedFileModel
  isLoading: boolean
  error?: AuthFetchError
  categoryId?: string
}

const DialogContent: React.FC<DialogContentProps> = ({ fileName, fileData, error, isLoading = false, categoryId }) => {
  const navigate = useNavigate()
  const location = useLocation()

  if (isLoading && !error) return <Loading />

  if (error) {
    if (error.response?.data.errno === FetchErrorCodes.FileDoesNotExist) {
      return <Typography values={{ fileName }}>{'downloads.protected-file-dialog.file-does-not-exist'}</Typography>
    }

    if (error.response?.data.errno === FetchErrorCodes.NoAccessToFile) {
      return <Typography values={{ fileName }}>{'downloads.protected-file-dialog.no-access-to-file'}</Typography>
    }

    return <Typography>{'downloads.protected-file-dialog.error-fetching-file'}</Typography>
  }

  if (fileData) {
    const categoryId = location.pathname.split('/').pop() || ''
    const directParent = fileData.parents.at(-1)

    if (categoryId !== directParent?.id.toString()) {
      navigate(`downloads/${directParent?.id}`)
    }

    return (
      <Box>
        <DownloadItemBreadcrumbs parentCategories={fileData.parents} onClickCategory={() => undefined} variant={'body1'} breakLastBreadcrumb />
        <Stack direction="row" spacing={1} alignItems="center" mb={2} mt={1}>
          <J2Documentcopy />
          <Typography variant={'h2'} order={0} fontWeight={'bold'}>
            {`${fileData.fileName}.${fileData.fileType}`}
          </Typography>
        </Stack>
      </Box>
    )
  }
}

const ProtectedFileDialog: React.FC<ProtectedFileDialogProps> = ({ deleteFileNameToken, onClose, protectedFileName }) => {
  const { t } = useCustomTranslation()

  const [searchParams, setSearchParams] = useSearchParams()
  const [protectedFile, isLoading, error] = useProtectedFile(protectedFileName)
  const { viewFile, downloadFile } = useDownloadItemActions()

  const onCloseHandler = (): void => {
    searchParams.delete(PROTECTED_FILE_QUERY_PARAM)
    setSearchParams(searchParams)
    deleteFileNameToken()
    onClose?.()
  }

  const getDialogActions = (): DialogActions => {
    if (protectedFile && !isLoading && !error) {
      return {
        primary: {
          name: t('downloads.protected-file-dialog.download'),
          onClick: () => downloadFile(protectedFile.downloadItemId),
        },
        additional: {
          name: t('downloads.protected-file-dialog.view'),
          onClick: () => viewFile(protectedFile.downloadItemId),
        },
        secondary: {
          name: t('common.cancel'),
          onClick: onCloseHandler,
        },
      }
    }

    return {
      primary: {
        name: t('common.close'),
        onClick: onCloseHandler,
        isLoading,
      },
    }
  }

  return (
    <Dialog title={t('downloads.protected-file-dialog.title')} maxWidth="sm" onClose={onCloseHandler} actions={getDialogActions()}>
      <DialogContent fileName={protectedFileName} fileData={protectedFile} isLoading={isLoading} error={error} />
    </Dialog>
  )
}

export default ProtectedFileDialog
